import { useCallback, useEffect, useRef } from 'react';

export interface NavigationSnapshot {
  businessId: string;
  filter: 'all' | 'messages' | 'new' | 'calls' | 'review';
  query: string;
  selectedId: string | null;
  mobileDetailOpen: boolean;
  businessPickerOpen: boolean;
  composeOpen: boolean;
  composeBusinessId: string;
  composeTo: string;
  composeText: string;
  callOpen: boolean;
  callBusinessId: string;
  callTo: string;
  settingsOpen: boolean;
  notificationsOpen: boolean;
  lookupOpen: boolean;
  lookupId: string | null;
  contactsOpen: boolean;
  contactId: string | null;
  contactBusinessId: string | null;
  contactPhoneE164: string | null;
}

interface HistoryNavigationState extends NavigationSnapshot {
  linewatchNavigation: true;
  depth: number;
}

function isHistoryNavigationState(value: unknown): value is HistoryNavigationState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<HistoryNavigationState>;
  return state.linewatchNavigation === true && typeof state.depth === 'number';
}

export function useNavigationHistory(
  snapshot: NavigationSnapshot,
  applySnapshot: (snapshot: NavigationSnapshot) => void,
  enabled: boolean,
) {
  const snapshotRef = useRef(snapshot);
  const applyRef = useRef(applySnapshot);
  const initializedRef = useRef(false);
  snapshotRef.current = snapshot;
  applyRef.current = applySnapshot;

  useEffect(() => {
    if (!enabled) return;
    const existing = window.history.state;
    if (isHistoryNavigationState(existing)) {
      applyRef.current(existing);
      snapshotRef.current = existing;
    } else {
      window.history.replaceState({ ...snapshotRef.current, linewatchNavigation: true, depth: 0 } satisfies HistoryNavigationState, '');
    }
    initializedRef.current = true;

    const handlePopState = (event: PopStateEvent) => {
      if (!isHistoryNavigationState(event.state)) return;
      snapshotRef.current = event.state;
      applyRef.current(event.state);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [enabled]);

  const push = useCallback((patch: Partial<NavigationSnapshot>) => {
    const currentHistory = window.history.state;
    const depth = isHistoryNavigationState(currentHistory) ? currentHistory.depth + 1 : 1;
    const next: HistoryNavigationState = {
      ...snapshotRef.current,
      ...patch,
      linewatchNavigation: true,
      depth,
    };
    window.history.pushState(next, '');
    snapshotRef.current = next;
    applyRef.current(next);
  }, []);

  const replace = useCallback((patch: Partial<NavigationSnapshot>) => {
    const currentHistory = window.history.state;
    const depth = isHistoryNavigationState(currentHistory) ? currentHistory.depth : 0;
    const next: HistoryNavigationState = {
      ...snapshotRef.current,
      ...patch,
      linewatchNavigation: true,
      depth,
    };
    window.history.replaceState(next, '');
    snapshotRef.current = next;
    applyRef.current(next);
  }, []);

  const sync = useCallback((patch: Partial<NavigationSnapshot>) => {
    if (!initializedRef.current) return;
    const currentHistory = window.history.state;
    if (!isHistoryNavigationState(currentHistory)) return;
    const next: HistoryNavigationState = { ...currentHistory, ...patch };
    window.history.replaceState(next, '');
    snapshotRef.current = { ...snapshotRef.current, ...patch };
  }, []);

  const dismiss = useCallback((fallbackPatch: Partial<NavigationSnapshot>) => {
    const currentHistory = window.history.state;
    if (isHistoryNavigationState(currentHistory) && currentHistory.depth > 0) {
      window.history.back();
      return;
    }
    replace(fallbackPatch);
  }, [replace]);

  return { push, replace, sync, dismiss };
}
